import { startTransition } from 'react';
import { Link } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import AsyncButton from '@wojtekmaj/react-async-button';
import T, { useTranslation } from '@wojtekmaj/react-t';

import { toast } from '@rewardopl/react-toast';

import Button from '@rewardopl/react-ui/button';
import InputField from '@rewardopl/react-ui/input_field';

import { get, post } from '@rewardopl/utils/network';

import { ButtonWrapper, Form } from './form.styles';

import {
  currentAccountRoleState,
  currentAccountState,
  currentBusinessState,
} from '../../../../store';

import type { Account, AccountRole, Business } from '@rewardopl/types';

const pendingConfig = {
  disabled: true,
};

export default function LoginForm() {
  const setCurrentAccount = useSetAtom(currentAccountState);
  const setCurrentAccountRole = useSetAtom(currentAccountRoleState);
  const setCurrentBusiness = useSetAtom(currentBusinessState);
  const loginErrorString = useTranslation('Failed to log in');
  const invalidUsernameOrPasswordString = useTranslation('Invalid username or password');

  const action1 = '/api/accounts/login';
  const action2 = '/api/account_roles/current';
  const action3 = '/api/businesses/current';

  async function onClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    event.preventDefault();

    const submitButton = event.currentTarget;
    const form = submitButton.form;

    if (!form || !form.reportValidity()) {
      return;
    }

    try {
      const account = (await post(action1, {
        body: new FormData(form),
      })) as Account;

      const [accountRole, business] = await Promise.all([
        get(action2) as Promise<AccountRole>,
        account.business_id ? (get(action3) as Promise<Business>) : Promise.resolve(null),
      ]);

      startTransition(() => {
        setCurrentAccount(account);
        setCurrentAccountRole(accountRole);
        if (business) {
          setCurrentBusiness(business);
        }
      });
    } catch (error) {
      if (error instanceof Error && error.message === 'Invalid username or password') {
        toast.error(invalidUsernameOrPasswordString);
        return;
      }

      toast.error(loginErrorString);
    }
  }

  return (
    <Form action={action1} method="POST">
      <InputField
        autoComplete="username"
        label="Email"
        maxLength={320}
        name="email"
        placeholder="email@example.com"
        type="email"
      />
      <InputField
        autoComplete="current-password"
        label="Password"
        name="password"
        placeholder="••••••••"
        type="password"
      />
      <ButtonWrapper>
        <AsyncButton
          as={Button}
          className="primary"
          onClick={onClick}
          pendingConfig={pendingConfig}
          type="submit"
        >
          <T>Login</T>
        </AsyncButton>
      </ButtonWrapper>
      <Link to="/password_reset">
        <T>Forgot password?</T>
      </Link>
    </Form>
  );
}
